import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Mic, Square, Send, Trash2, Loader2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

import { API_URL } from '../../config/api';

export default function VoiceRecorder({ onSendVoice, onTranscribe, disabled }) {
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [duration, setDuration] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const [transcription, setTranscription] = useState('');
  
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const streamRef = useRef(null);

  useEffect(() => {
    return () => {
      // Cleanup on unmount
      if (timerRef.current) clearInterval(timerRef.current);
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      if (audioUrl) URL.revokeObjectURL(audioUrl);
    };
  }, [audioUrl]);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/mp4'
      });
      
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { 
          type: mediaRecorder.mimeType 
        });
        setAudioBlob(blob);
        setAudioUrl(URL.createObjectURL(blob));
      };

      mediaRecorder.start(100); // Collect data every 100ms
      setIsRecording(true);
      setDuration(0);
      
      // Start timer
      timerRef.current = setInterval(() => {
        setDuration(prev => prev + 1);
      }, 1000);
      
    } catch (error) {
      console.error('Error accessing microphone:', error);
      toast.error("Impossible d'accéder au microphone. Vérifiez les permissions.");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    }
  };

  const cancelRecording = () => {
    stopRecording();
    setAudioBlob(null);
    setAudioUrl(null);
    setDuration(0);
    setTranscription('');
    chunksRef.current = [];
  };

  const handleSend = async () => {
    if (!audioBlob) return;
    
    setIsProcessing(true);
    
    try {
      const token = localStorage.getItem('client_token');
      const formData = new FormData();
      
      // Determine file extension based on mime type
      const ext = audioBlob.type.includes('webm') ? 'webm' : 'mp4';
      formData.append('file', audioBlob, `voice_note.${ext}`);
      formData.append('transcribe', 'true');

      const response = await fetch(`${API_URL}/api/files/voice/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        
        // Call parent callback with voice data
        if (onSendVoice) {
          onSendVoice({
            file_url: data.file_url,
            file_name: data.file_name,
            transcription: data.transcription,
            duration: duration
          });
        }
        
        // Reset state
        cancelRecording();
        toast.success('Note vocale envoyée');
      } else {
        const error = await response.json();
        toast.error(error.detail || "Erreur lors de l'envoi");
      }
    } catch (error) {
      console.error('Error sending voice note:', error);
      toast.error("Erreur lors de l'envoi de la note vocale");
    } finally {
      setIsProcessing(false);
    }
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Recording in progress
  if (isRecording) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="flex items-center gap-3 bg-red-50 rounded-full px-4 py-2"
      >
        <div className="flex items-center gap-2">
          <span className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
          <span className="text-sm font-medium text-red-600">
            {formatDuration(duration)}
          </span>
        </div>
        <button
          onClick={stopRecording}
          className="p-2 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
          title="Arrêter l'enregistrement"
        >
          <Square className="w-4 h-4" />
        </button>
        <button
          onClick={cancelRecording}
          className="p-2 text-red-500 hover:bg-red-100 rounded-full transition-colors"
          title="Annuler"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </motion.div>
    );
  }

  // Recording complete, ready to send
  if (audioBlob && audioUrl) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="flex items-center gap-3 bg-slate-100 rounded-full px-4 py-2"
      >
        <audio src={audioUrl} controls className="h-8 max-w-[150px]" />
        <span className="text-xs text-slate-500">{formatDuration(duration)}</span>
        
        <button
          onClick={cancelRecording}
          className="p-2 text-slate-500 hover:bg-slate-200 rounded-full transition-colors"
          title="Supprimer"
          disabled={isProcessing}
        >
          <Trash2 className="w-4 h-4" />
        </button>
        
        <button
          onClick={handleSend}
          disabled={isProcessing}
          className="p-2 bg-rm-primary text-white rounded-full hover:bg-rm-primary/90 transition-colors disabled:opacity-50"
          title="Envoyer"
        >
          {isProcessing ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Send className="w-4 h-4" />
          )}
        </button>
      </motion.div>
    );
  }

  // Initial state - record button
  return (
    <button
      onClick={startRecording}
      disabled={disabled}
      className="p-2 hover:bg-slate-100 rounded-lg text-slate-400 hover:text-slate-600 transition-colors disabled:opacity-50"
      title="Enregistrer une note vocale"
    >
      <Mic className="w-5 h-5" />
    </button>
  );
}
